import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Truck, MapPin, Star } from 'lucide-react';

const stats = [
  { label: 'Deliveries Completed', end: 50000, suffix: 'K+', divisor: 1000, color: 'orange', icon: TrendingUp, desc: 'Successful shipments pan-India' },
  { label: 'Fleet Size',           end: 1200,  suffix: '+',  divisor: 1,    color: 'dark',   icon: Truck,      desc: 'Premium vehicles ready to deploy' },
  { label: 'Cities Covered',       end: 250,   suffix: '+',  divisor: 1,    color: 'orange', icon: MapPin,     desc: 'Active logistics network nodes' },
  { label: 'Client Satisfaction',  end: 99,    suffix: '%',  divisor: 1,    color: 'dark',   icon: Star,       desc: 'Rated by verified customers' },
];

function CountUp({ end, suffix, divisor, color, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  const display = divisor > 1 ? (count / divisor).toFixed(1) : count;

  return (
    <h3 ref={ref} className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-2 tabular-nums ${
      color === 'orange' ? 'text-brand-orange' : 'text-brand-black'
    }`}>
      {display}{suffix}
    </h3>
  );
}

const Stats = () => (
  <section className="bg-white py-14 sm:py-20 px-4 sm:px-6 border-y border-black/5 relative overflow-hidden">
    <motion.div
      animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[700px] h-48 sm:h-72 bg-brand-orange rounded-full blur-[120px] pointer-events-none"
    />

    <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 relative z-10">
      {stats.map((stat, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: idx * 0.12, type: 'spring', stiffness: 100 }}
          whileHover={{ y: -8, scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="relative p-4 sm:p-5 md:p-8 rounded-[1.5rem] sm:rounded-[2rem] bg-brand-bg border border-black/5 shadow-sm hover:border-brand-orange/40 hover:shadow-xl transition-all text-center group cursor-default overflow-hidden"
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[2rem]"
          />
          <div className="flex justify-center mb-2 sm:mb-3">
            <motion.div
              whileHover={{ rotate: 15, scale: 1.2 }}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center group-hover:bg-brand-orange transition-colors duration-500"
            >
              <stat.icon size={16} className="text-brand-orange group-hover:text-white transition-colors duration-500" />
            </motion.div>
          </div>
          <CountUp end={stat.end} suffix={stat.suffix} divisor={stat.divisor} color={stat.color} />
          <motion.div
            initial={{ width: '2.5rem' }}
            whileInView={{ width: '2.5rem' }}
            whileHover={{ width: '5rem' }}
            className="h-[2px] bg-brand-orange mx-auto mb-2 sm:mb-3 transition-all duration-500 group-hover:w-20"
          />
          <p className="text-brand-muted text-[9px] sm:text-[10px] md:text-xs font-black tracking-[0.15em] sm:tracking-[0.25em] uppercase leading-relaxed">
            {stat.label}
          </p>
          <p className="text-slate-400 text-[9px] mt-1 hidden md:block">{stat.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

export default Stats;
